import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { ChartingCredentialStage } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useMemo } from "react";

import { type ExtendedExternalIntegrationType, ExtendedExternalIntegrationTypes } from "../types";
import { useGetWorkplaceExternalIntegrations } from "./useGetWorkplaceExternalIntegrations";

interface UseGetWorkplaceEnabledConfigurationsParams {
  workplaceId: string;
}

export function useGetWorkplaceEnabledConfigurations(
  params: UseGetWorkplaceEnabledConfigurationsParams
): ExtendedExternalIntegrationType[] {
  const { workplaceId } = params;

  const { data: externalIntegrations } = useGetWorkplaceExternalIntegrations({ workplaceId });

  const { stage } = useCbhFlag(CbhFeatureFlag.CREDENTIAL_SHARING, {
    defaultValue: {
      stage: ChartingCredentialStage.OFF,
      enableOnboardingView: false,
      shiftStartingSoonThresholdInHours: 24,
    },
  });

  const { enabled: touchpointIntegrationEnabled } = useCbhFlag(
    CbhFeatureFlag.TOUCHPOINT_INTEGRATION,
    {
      defaultValue: {
        enabled: false,
      },
    }
  );

  return useMemo(() => {
    const types: ExtendedExternalIntegrationType[] = (externalIntegrations ?? [])
      .filter((integration) => integration.isEnabled)
      .map((integration) => integration.type);

    if (
      stage === ChartingCredentialStage.CHARTING_AND_TIME_CLOCK ||
      stage === ChartingCredentialStage.TIME_CLOCK_ONLY
    ) {
      types.push(ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE);
    }

    if (touchpointIntegrationEnabled) {
      types.push(ExtendedExternalIntegrationTypes.MESH);
    }

    return types;
  }, [externalIntegrations, stage, touchpointIntegrationEnabled]);
}
